import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    Grid,
    Box,
    Typography,
    Chip,
    Card,
    CardContent,
    Stack,
    Avatar,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from "@mui/material";
import { updateTodoTask } from "../../../services/todoTask";
import { ToastError, ToastSuccess } from "../../../utils/Toaster/Toaster";
// Icons
import { IoFlagOutline, IoTimeOutline, IoArrowForward } from "react-icons/io5";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { ImAttachment } from "react-icons/im";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import CloseIcon from "@mui/icons-material/Close";

// Assuming these images might not be available in TodoTask folder, I'll use standard MUI icons or placeholders if needed.
// But to match the request exactly, I'll try to use the same paths if they are generic assets.
import ConfirmDelete_Icon from "../../../assets/images/CommonTableImg/ConfirmDelete_Icon.svg";
import dayjs from "dayjs";

const TodoTaskBoard = ({ data, handleView, handleEdit, handleDelete, handleHistory, refreshData, userId }) => {
    // Unique keys to avoid collision between Priority 1 (Medium) and Status 1 (Completed)
    const COLUMN_KEYS = {
        LOW: "priority-0",
        MEDIUM: "priority-1",
        HIGH: "priority-2",
        COMPLETED: "status-1",
        CANCELED: "status-2",
        ACKNOWLEDGE: "status-3",
    };

    const COLUMN_ORDER = [
        COLUMN_KEYS.LOW,
        COLUMN_KEYS.MEDIUM,
        COLUMN_KEYS.HIGH,
        COLUMN_KEYS.COMPLETED,
        COLUMN_KEYS.CANCELED,
        COLUMN_KEYS.ACKNOWLEDGE
    ];

    const [columns, setColumns] = useState({
        [COLUMN_KEYS.LOW]: [],
        [COLUMN_KEYS.MEDIUM]: [],
        [COLUMN_KEYS.HIGH]: [],
        [COLUMN_KEYS.COMPLETED]: [],
        [COLUMN_KEYS.CANCELED]: [],
        [COLUMN_KEYS.ACKNOWLEDGE]: [],
    });

    // Delete Confirmation State
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteTask, setDeleteTask] = useState(null);

    const handleDeleteClick = (task) => {
        setDeleteTask(task);
        setOpenDelete(true);
    };

    const confirmDelete = () => {
        if (deleteTask) {
            handleDelete(deleteTask);
            setOpenDelete(false);
            setDeleteTask(null);
        }
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
        setDeleteTask(null);
    };

    useEffect(() => {
        const newColumns = {
            [COLUMN_KEYS.LOW]: [],
            [COLUMN_KEYS.MEDIUM]: [],
            [COLUMN_KEYS.HIGH]: [],
            [COLUMN_KEYS.COMPLETED]: [],
            [COLUMN_KEYS.CANCELED]: [],
            [COLUMN_KEYS.ACKNOWLEDGE]: [],
        };

        // Sort data by priorityLevel ascending
        const sortedData = [...data].sort((a, b) => (a.priorityLevel || 0) - (b.priorityLevel || 0));

        sortedData.forEach(task => { // TodoTask.js: PENDING:0, COMPLETED:1, CANCELED:2, ACKNOWLEDGE:3
            const status = parseInt(task.status);

            if (status === 1) { // Completed
                newColumns[COLUMN_KEYS.COMPLETED].push(task);
            } else if (status === 2) { // Canceled
                newColumns[COLUMN_KEYS.CANCELED].push(task);
            } else if (status === 3) { // Acknowledge
                newColumns[COLUMN_KEYS.ACKNOWLEDGE].push(task);
            } else {
                // Pending (0) -> Priority Columns
                const priorityKey = `priority-${task.priority}`; // priority: 0, 1, 2
                if (newColumns[priorityKey] !== undefined) {
                    newColumns[priorityKey].push(task);
                } else {
                    newColumns[COLUMN_KEYS.LOW].push(task);
                }
            }
        });

        setColumns(newColumns);
    }, [data]);

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        const sourceColId = source.droppableId;
        const destColId = destination.droppableId;

        // Optimistic update
        const sourceCol = [...columns[sourceColId]];
        const destCol = sourceColId === destColId ? sourceCol : [...columns[destColId]];

        const [movedTask] = sourceCol.splice(source.index, 1);

        let updatedTask = { ...movedTask };
        let payload = {};

        // Helper to determine if column is a Status column (Completed/Canceled)
        const isStatusColumn = (colId) => colId.startsWith("status-");

        // Scenario 1: Drag to Status Column (Completed/Canceled/Acknowledge)
        if (isStatusColumn(destColId)) {
            let newStatus;
            if (destColId === COLUMN_KEYS.COMPLETED) newStatus = 1;
            else if (destColId === COLUMN_KEYS.CANCELED) newStatus = 2;
            else if (destColId === COLUMN_KEYS.ACKNOWLEDGE) newStatus = 3;

            updatedTask.status = newStatus;

            // if (newStatus === 1) { // Completed
            //     updatedTask.completionDate = new Date().toISOString();
            // }

            payload = {
                status: newStatus,
                createdBy: userId
            };
        }
        // Scenario 2: Drag from Status Column to Priority Column (Revert to Pending)
        else if (isStatusColumn(sourceColId) && !isStatusColumn(destColId)) {
            const newPriority = parseInt(destColId.split("-")[1]);

            updatedTask.status = 0; // Set to Pending
            updatedTask.priority = newPriority;

            payload = {
                status: 0, // Pending
                priority: newPriority,
                newIndex: destination.index,
                priorityLevel: destination.index,
                createdBy: userId
            };
        }
        // Scenario 3: Drag between Priority Columns (Status remains Pending)
        else {
            const newPriority = parseInt(destColId.split("-")[1]);
            updatedTask.priority = newPriority;

            payload = {
                priority: newPriority,
                newIndex: destination.index,
                priorityLevel: destination.index,
                createdBy: userId
            };
        }

        destCol.splice(destination.index, 0, updatedTask);

        setColumns({
            ...columns,
            [sourceColId]: sourceColId === destColId ? destCol : sourceCol,
            [destColId]: destCol,
        });

        // API Call
        try {
            const formData = new FormData();
            formData.append("bodyData", JSON.stringify(payload));

            const res = await updateTodoTask(draggableId, formData);
            if (res.data && res.data.message) {
                ToastSuccess(res.data.message || "TodoTask updated successfully");
                refreshData();
            }
        } catch (error) {
            console.error("Drag update error:", error);
            ToastError(error.response.data.message || "Failed to update TodoTask");
            refreshData();
        }
    };

    const getColumnTitle = (id) => {
        switch (id) {
            case COLUMN_KEYS.LOW: return "Low";
            case COLUMN_KEYS.MEDIUM: return "Medium";
            case COLUMN_KEYS.HIGH: return "High";
            case COLUMN_KEYS.COMPLETED: return "Completed";
            case COLUMN_KEYS.CANCELED: return "Canceled";
            case COLUMN_KEYS.ACKNOWLEDGE: return "Acknowledge";
            default: return "Unknown";
        }
    };

    const getColumnColor = (id) => {
        switch (id) {
            case COLUMN_KEYS.LOW: return "#21A600"; // Green
            case COLUMN_KEYS.MEDIUM: return "#D7BB21"; // Yellow
            case COLUMN_KEYS.HIGH: return "#D50707"; // Red
            case COLUMN_KEYS.COMPLETED: return "#4CAF50"; // Green for Completed
            case COLUMN_KEYS.CANCELED: return "#F44336"; // Red for Canceled
            case COLUMN_KEYS.ACKNOWLEDGE: return "#2196F3"; // Blue for Acknowledge
            default: return "#6C757D";
        }
    };

    const truncateText = (text, maxLength = 30) => {
        return text
            ? text.length > maxLength
                ? `${text.substring(0, maxLength)}...`
                : text
            : "-";
    };

    const formatDeadline = (dateStr, status) => {
        if (!dateStr) return { text: "-", color: "grey", showIcon: false };

        // If completed, show "Completed"
        if (status === 1) {
            return { text: "Completed", color: "#4CAF50", showIcon: false };
        }

        const today = dayjs().startOf("day");
        const deadline = dayjs(dateStr).startOf("day");
        const diffDays = deadline.diff(today, "day");

        // Format is what the user wants: "DD MMM" (e.g. 12 Dec)
        const formattedDate = dayjs(dateStr).format("DD MMM");

        if (diffDays < 0) return { text: formattedDate, color: "red", showIcon: true };
        if (diffDays === 0) return { text: formattedDate, color: "orange", showIcon: true };
        return { text: formattedDate, color: "green", showIcon: true };
    };

    const getStatusLabel = (status) => {
        switch (parseInt(status)) {
            case 0: return "Pending";
            case 1: return "Completed";
            case 2: return "Canceled";
            case 3: return "Acknowledge";
            default: return "Pending";
        }
    };

    const renderTaskCard = (task, index, columnId) => {
        const { text: dateText, color: dateColor, showIcon: dateIcon } = formatDeadline(task.completionDate, task.status);

        return (
            <Draggable key={task._id} draggableId={task._id} index={index}>
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => handleView({ ...task, isView: true })}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                boxShadow: "none",
                                border: "1px solid #E5E9F1", // Thinner border
                                paddingBottom: "0px",
                                height: "100%",
                                borderRadius: "8px", // Smaller radius
                                mb: 1, // Reduced margin bottom
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    borderColor: "#bbb",
                                }
                            }}
                        >
                            {/* Compact Padding */}
                            <CardContent sx={{ padding: "10px !important" }}>
                                {/* Header Row */}
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: "4px", // Reduced margin
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            gap: "4px",
                                        }}
                                    >
                                        <IoFlagOutline
                                            size={12} // Smaller icon
                                            color={getColumnColor(columnId)}
                                        />
                                        {/* Compact Badge */}
                                        <Typography
                                            sx={{
                                                fontSize: "10px", // Smaller font
                                                fontWeight: 600,
                                                fontFamily: "Inter, sans-serif",
                                                color: getColumnColor(columnId),
                                                bgcolor: getColumnColor(columnId) + "15",
                                                padding: "1px 6px",
                                                borderRadius: "4px",
                                                lineHeight: 1.2
                                            }}
                                        >
                                            #{task.priorityLevel || index + 1}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.4}>
                                        <Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: 500,
                                                color: "#888",
                                                fontFamily: "Inter, sans-serif",
                                            }}
                                        >
                                            {getStatusLabel(task.status)}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <Divider
                                    style={{
                                        margin: "4px 0", // Tighter divider
                                        border: "0.5px solid #E5E9F1",
                                    }}
                                />

                                {/* Title + Time Row */}
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: "4px", mb: "2px" }}>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            fontFamily: "Inter, sans-serif",
                                            color: "#333",
                                            flex: 1,
                                            mr: 1
                                        }}
                                    >
                                        {task.title}
                                    </Typography>

                                    {/* Date/Time (Moved from Footer) */}
                                    {task.completionDate && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.4}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {dateIcon && (
                                                <AccessAlarmIcon sx={{ fontSize: 12, color: dateColor }} />
                                            )}
                                            <Typography
                                                sx={{ color: dateColor, fontSize: 10, fontWeight: 500 }}
                                            >
                                                {dateText}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>

                                {/* Description Restored */}
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        color: "#687280",
                                        fontWeight: 400,
                                        fontFamily: "Inter, sans-serif",
                                        mb: "8px",
                                        lineHeight: 1.3
                                    }}
                                >
                                    {truncateText(task.description || "", 40)}
                                </Typography>

                                {/* Footer */}
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {/* Actions (Moved from Title Row) */}
                                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleHistory(task);
                                                }}
                                                sx={{
                                                    padding: "2px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    color: "#666",
                                                    "&:hover": {
                                                        backgroundColor: "#E3F2FD",
                                                        color: "#1976D2"
                                                    }
                                                }}
                                            >
                                                <IoTimeOutline size={12} />
                                            </Box>
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(task);
                                                }}
                                                sx={{
                                                    padding: "2px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    color: "#666",
                                                    "&:hover": {
                                                        backgroundColor: "#E3F2FD",
                                                        color: "#1976D2"
                                                    }
                                                }}
                                            >
                                                <FiEdit2 size={12} />
                                            </Box>
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(task);
                                                }}
                                                sx={{
                                                    padding: "2px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    color: "#666",
                                                    "&:hover": {
                                                        backgroundColor: "#FFEBEE",
                                                        color: "#D32F2F"
                                                    }
                                                }}
                                            >
                                                <RiDeleteBinLine size={12} />
                                            </Box>
                                        </Stack>

                                        {/* Attachment - Only show if exists */}
                                        {task.file && task.file.length > 0 && (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={0.5}
                                                sx={{ color: "#666" }}
                                            >
                                                <ImAttachment size={10} />
                                                <Typography fontSize={"10px"}>
                                                    {task.file.length}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>


                                    {/* Project Name & Avatar */}
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography
                                            sx={{
                                                fontSize: "10px", // Small font for project tag
                                                color: "#888",
                                                fontWeight: 500,
                                                fontFamily: "Inter, sans-serif",
                                                maxWidth: "100px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {task.projectName}
                                        </Typography>

                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Tooltip
                                                title={`Created by: ${task.createdByName || "Unknown"}`}
                                                arrow
                                                placement="top"
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: "22px",
                                                        height: "22px",
                                                        fontSize: "9px",
                                                        backgroundColor: "#F3F4F6",
                                                        color: "#374151",
                                                        fontWeight: 700,
                                                        border: "1px solid #E5E7EB"
                                                    }}
                                                >
                                                    {task.createdByName ? (
                                                        `${task.createdByName.charAt(0)}${task.createdByName.split(' ')[1]?.charAt(0) || ''}`.toUpperCase()
                                                    ) : (
                                                        "?"
                                                    )}
                                                </Avatar>
                                            </Tooltip>

                                            <IoArrowForward size={10} color="#9CA3AF" />

                                            <Tooltip
                                                title={`Assigned to: ${task.assignedToName || "Unassigned"}`}
                                                arrow
                                                placement="top"
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: "22px",
                                                        height: "22px",
                                                        fontSize: "9px",
                                                        backgroundColor: "#ECFDF5",
                                                        color: "#059669",
                                                        fontWeight: 700,
                                                        border: "1px solid #D1FAE5"
                                                    }}
                                                >
                                                    {task.assignedToName ? (
                                                        `${task.assignedToName.charAt(0)}${task.assignedToName.split(' ')[1]?.charAt(0) || ''}`.toUpperCase()
                                                    ) : (
                                                        <PermIdentityOutlinedIcon sx={{ fontSize: 12, color: "#059669" }} />
                                                    )}
                                                </Avatar>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>

                                </Stack>

                                {/* Actions */}

                            </CardContent>
                        </Card>
                    </div>
                )}
            </Draggable>
        );
    };


    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    width: "100%",
                    height: "calc(100vh - 220px)", // Fixed height to fit viewport
                    padding: "10px 0",
                    gap: "16px",
                    // Custom Scrollbar for horizontal axis
                    "&::-webkit-scrollbar": { height: "8px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": { background: "#ddd", borderRadius: "4px" },
                    "&::-webkit-scrollbar-thumb:hover": { background: "#bbb" },
                }}
            >
                {COLUMN_ORDER.map((columnId) => (
                    <Box
                        sx={{
                            flex: "0 0 290px",
                            minWidth: "290px",
                            height: "100%", // Full height of container
                            display: "flex",
                            flexDirection: "column",
                            bgcolor: "#f4f5f7",
                            borderRadius: "12px",
                            border: "1px solid #ebecf0"
                        }}
                        key={columnId}
                    >
                        <Droppable droppableId={columnId}>
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column"
                                    }}
                                >
                                    {/* Column Header - Sticky / Fixed */}
                                    <Stack
                                        sx={{
                                            padding: "10px",
                                            backgroundColor: "#FBFBFB",
                                            borderRadius: "12px 12px 0 0",
                                            border: "1px solid #E5E9F1",
                                            flexShrink: 0
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                border: "1px solid #E5E9F1",
                                                borderRadius: "6px",
                                                padding: "6px 8px",
                                                flexDirection: "row",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontSize: "16px",
                                                    fontWeight: 600,
                                                    color: "#333",
                                                    fontFamily: "Inter, sans-serif",
                                                }}
                                            >
                                                {getColumnTitle(columnId)}
                                            </Typography>
                                            <Stack
                                                sx={{
                                                    backgroundColor: `${getColumnColor(columnId)}26`,
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    padding: "2px 8px",
                                                    borderRadius: "16px",
                                                    minWidth: "28px",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: "bold",
                                                        color: getColumnColor(columnId),
                                                    }}
                                                >
                                                    {columns[columnId].length}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    {/* Scrollable Card Area */}
                                    <Box
                                        sx={{
                                            flexGrow: 1, // Fill remaining space
                                            overflowY: "auto", // Independent scroll
                                            padding: "10px",
                                            backgroundColor: "#f4f5f7",
                                            borderRadius: "0 0 12px 12px",
                                            // Custom Scrollbar
                                            "&::-webkit-scrollbar": { width: "6px" },
                                            "&::-webkit-scrollbar-track": { background: "transparent" },
                                            "&::-webkit-scrollbar-thumb": {
                                                background: "#ddd",
                                                borderRadius: "3px",
                                            },
                                            "&::-webkit-scrollbar-thumb:hover": { background: "#bbb" },
                                        }}
                                    >
                                        {columns[columnId].map((task, index) =>
                                            renderTaskCard(task, index, columnId)
                                        )}
                                        {provided.placeholder}
                                    </Box>
                                </div>
                            )}
                        </Droppable>
                    </Box>
                ))}
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                PaperProps={{
                    style: {
                        width: "100%",
                        height: "fit-content",
                        padding: 20,
                        borderRadius: "12px",
                    },
                }}
                fullWidth
                maxWidth="xs"
            >
                <Stack alignItems={"end"} justifyContent={"end"}>
                    <CloseIcon
                        onClick={handleCloseDelete}
                        sx={{ cursor: "pointer", color: "#666" }}
                    />
                </Stack>
                <Stack alignItems={"center"}>
                    <Stack padding={1}>
                        <img
                            src={ConfirmDelete_Icon}
                            alt="ConfirmDelete_Icon"
                            height={50}
                        />
                    </Stack>
                    <Typography
                        sx={{ fontSize: "20px", fontWeight: 700, color: "#3F3F46" }}
                    >
                        Are you sure ?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            textAlign: "center",
                            padding: "0 20px",
                            color: "#666",
                            mt: 1,
                            mb: 2
                        }}
                    >
                        Are you sure you want to delete this task? This action cannot be undone.
                    </Typography>
                    <Stack direction={"row"} sx={{ marginTop: 2, gap: 2 }}>
                        <Button
                            onClick={handleCloseDelete}
                            sx={{
                                color: "#666",
                                border: "1px solid #E5E9F1",
                                padding: "8px 24px",
                                borderRadius: "8px",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#f5f5f5" }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            sx={{
                                color: "white !important",
                                backgroundColor: "#D32F2F", // Red for Delete
                                padding: "8px 24px",
                                borderRadius: "8px",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#B71C1C" }
                            }}
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Stack>
            </Dialog>
        </DragDropContext>
    );
};

export default TodoTaskBoard;
